import { Divider, useModalState } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { useWorkerEventsForRating } from "@src/appV2/redesign/Tiers/api/useWorkerEventsForRating";
import { LateCancelRatingExplanationCard } from "@src/appV2/redesign/Tiers/RatingsSection/LateCancelRatingExplanationCard";
import { RatingBottomSheet } from "@src/appV2/redesign/Tiers/RatingsSection/RatingBottomSheet";
import { RatingExplanationCard } from "@src/appV2/redesign/Tiers/RatingsSection/RatingExplanationCard";
import { RatingsExplanationTitle } from "@src/appV2/redesign/Tiers/RatingsSection/RatingsExplanationTitle";
import { type NormalizedTierRatings } from "@src/appV2/redesign/Tiers/utils/types";
import pluralize from "pluralize";

interface RatingsExplanationCardProps {
  normalizedTierRatings: NormalizedTierRatings;
  lateCancelThresholdInHours: number;
  workerTimeZone: string;
  workerId: string;
}

export function RatingsExplanationCard(props: RatingsExplanationCardProps) {
  const { normalizedTierRatings, lateCancelThresholdInHours, workerTimeZone, workerId } = props;
  const { workerRatings, targetRatings, workerTier, eventsToCount } = normalizedTierRatings;
  const {
    onTimeArrival: workerOnTimeArrivals,
    shiftCompletionWithoutCancel: workerShiftCompletionsWithoutCancel,
    shiftCompletionWithoutLateCancel: workerShiftCompletionsWithoutLateCancel,
  } = workerRatings;
  const {
    onTimeArrival: targetOnTimeArrivals,
    shiftCompletionWithoutCancel: targetShiftCompletionsWithoutCancel,
    shiftCompletionWithoutLateCancel: targetShiftCompletionsWithoutLateCancel,
  } = targetRatings;
  const { workerEvents: onTimeArrivalWorkerEvents } = useWorkerEventsForRating("onTimeArrival");
  const { workerEvents: completionWorkerEvents } = useWorkerEventsForRating(
    "shiftCompletionWithoutCancel"
  );
  const { workerEvents: lateCancelWorkerEvents } = useWorkerEventsForRating(
    "shiftCompletionWithoutLateCancel"
  );
  const onTimeArrivalsModalState = useModalState();
  const shiftCompletionsWithoutCancelModalState = useModalState();
  const shiftCompletionsWithoutLateCancelModalState = useModalState();

  return (
    <Box>
      <Stack direction="column" gap={7}>
        <RatingsExplanationTitle workerTier={workerTier} />
        <RatingExplanationCard
          workerNormalizedRating={workerOnTimeArrivals}
          targetNormalizedRating={targetOnTimeArrivals}
          header="On-time arrival rate"
          ratingType="onTimeArrival"
          onClick={() => {
            onTimeArrivalsModalState.openModal();
          }}
        />
        <Divider />
        <RatingExplanationCard
          workerNormalizedRating={workerShiftCompletionsWithoutCancel}
          targetNormalizedRating={targetShiftCompletionsWithoutCancel}
          header="Completion rate"
          ratingType="shiftCompletionWithoutCancel"
          onClick={() => {
            shiftCompletionsWithoutCancelModalState.openModal();
          }}
        />
        <Divider />
        <LateCancelRatingExplanationCard
          workerNormalizedRating={workerShiftCompletionsWithoutLateCancel}
          targetNormalizedRating={targetShiftCompletionsWithoutLateCancel}
          header="Late cancels"
          onClick={() => {
            shiftCompletionsWithoutLateCancelModalState.openModal();
          }}
        />
      </Stack>
      <RatingBottomSheet
        title="On-time arrival rate"
        description={`The percentage of your last ${eventsToCount} worked shifts where you arrived on or before the scheduled start time`}
        tips={[
          "Leave 15 minutes earlier than your GPS suggests to account for unexpected delays",
          "Preview the commute to a new workplace before booking there",
        ]}
        workerEvents={onTimeArrivalWorkerEvents}
        modalState={onTimeArrivalsModalState}
        ratingDisplay={workerOnTimeArrivals.display}
        workerTimeZone={workerTimeZone}
        ratingType="onTimeArrival"
        workerId={workerId}
        workerTier={workerTier}
      />
      <RatingBottomSheet
        title="Completion rate"
        description={`The percentage of your last ${eventsToCount} bookings that you worked instead of cancelling`}
        tips={[
          "Only book when you're sure you have no conflicts",
          "Once you book, lock it into your calendar and plan around it",
        ]}
        workerEvents={completionWorkerEvents}
        modalState={shiftCompletionsWithoutCancelModalState}
        ratingDisplay={workerShiftCompletionsWithoutCancel.display}
        workerTimeZone={workerTimeZone}
        ratingType="shiftCompletionWithoutCancel"
        workerId={workerId}
        workerTier={workerTier}
      />
      <RatingBottomSheet
        title={`Late ${pluralize("cancel", workerShiftCompletionsWithoutLateCancel.rating)}`}
        description={`The number of your last ${eventsToCount} bookings where you cancelled within ${lateCancelThresholdInHours} ${pluralize(
          "hour",
          lateCancelThresholdInHours
        )} of the shift start instead of working.`}
        tips={[
          "If you have to cancel, do it as early as possible",
          "Once you book, lock it into your calendar and plan around it",
        ]}
        workerEvents={lateCancelWorkerEvents}
        modalState={shiftCompletionsWithoutLateCancelModalState}
        ratingDisplay={workerShiftCompletionsWithoutLateCancel.display}
        workerTimeZone={workerTimeZone}
        ratingType="shiftCompletionWithoutLateCancel"
        workerId={workerId}
        workerTier={workerTier}
      />
    </Box>
  );
}
